import React, { useState, useEffect } from 'react';

// Typewriter hook — cycles through titles
const useTypewriter = (words, typingSpeed = 80, pauseMs = 1800, deleteSpeed = 45) => {
    const [displayed, setDisplayed] = useState('');
    const [wordIdx, setWordIdx]     = useState(0);
    const [deleting, setDeleting]   = useState(false);

    useEffect(() => {
        const current = words[wordIdx % words.length];
        let timeout;
        if (!deleting && displayed.length < current.length) {
            timeout = setTimeout(() => setDisplayed(current.slice(0, displayed.length + 1)), typingSpeed);
        } else if (!deleting && displayed.length === current.length) {
            timeout = setTimeout(() => setDeleting(true), pauseMs);
        } else if (deleting && displayed.length > 0) {
            timeout = setTimeout(() => setDisplayed(current.slice(0, displayed.length - 1)), deleteSpeed);
        } else if (deleting && displayed.length === 0) {
            setDeleting(false);
            setWordIdx(i => i + 1);
        }
        return () => clearTimeout(timeout);
    }, [displayed, deleting, wordIdx, words, typingSpeed, pauseMs, deleteSpeed]);

    return displayed;
};

const TITLES = ['AI Engineer', 'ML Researcher', 'Team Lead', 'Full-Stack Builder'];

const Home = () => {
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [resumeEmail, setResumeEmail] = useState('');
    const [resumeMessage, setResumeMessage] = useState('');
    const [isSubmittingResume, setIsSubmittingResume] = useState(false);
    const [heroVisible, setHeroVisible] = useState(false);
    const typewriterText = useTypewriter(TITLES);

    useEffect(() => {
        // Trigger hero entrance after a short paint delay
        const t = setTimeout(() => setHeroVisible(true), 100);
        return () => clearTimeout(t);
    }, []);

    const handleResumeRequest = () => {
        setShowResumeModal(true);
        // Track resume interest
        if (window.gtag) {
            window.gtag('event', 'resume_interest', {
                event_category: 'engagement',
                event_label: 'resume_modal_opened'
            });
        }
    };

    const handleResumeDownload = async (e) => {
        e.preventDefault();

        if (!resumeEmail) {
            setResumeMessage('Please enter your email address');
            return;
        }

        setIsSubmittingResume(true);

        try {
            // EmailJS configuration from environment variables
            const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_s49ll6k';
            const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_jrj9r27';
            const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'Kg-EjnaLJhsZDRAyl';

            const emailjs = await import('emailjs-com');

            // Send notification to yourself
            await emailjs.default.send(serviceID, templateID, {
                user_name: 'Resume Request',
                user_email: resumeEmail,
                user_subject: 'Resume Download Request',
                user__message: `Someone requested your resume download. Email: ${resumeEmail}`,
            }, publicKey);

            // Track successful resume request
            if (window.gtag) {
                window.gtag('event', 'resume_request', {
                    event_category: 'conversion',
                    event_label: 'resume_email_submitted',
                    value: 1
                });
            }

            // Show thank you message instead of downloading
            setResumeMessage('✅ Thank you for your interest! I will email you my resume within 24 hours.');
            setResumeEmail('');

            // Close modal after 3 seconds
            setTimeout(() => {
                setShowResumeModal(false);
                setResumeMessage('');
            }, 3000);

        } catch (error) {
            console.error('Resume request error:', error);
            setResumeMessage('✅ Thank you for your interest! I will email you my resume within 24 hours.');

            // Still show success message even if email fails
            setTimeout(() => {
                setShowResumeModal(false);
                setResumeMessage('');
            }, 3000);
        } finally {
            setIsSubmittingResume(false);
        }
    };

    const marqueeItems = [
        { icon: 'ri-brain-line',       label: 'Medical AI' },
        { icon: 'ri-robot-line',        label: 'LLM Agents' },
        { icon: 'ri-eye-line',          label: 'Computer Vision' },
        { icon: 'ri-leaf-line',         label: 'AgriTech' },
        { icon: 'ri-building-line',     label: 'Enterprise Automation' },
        { icon: 'ri-code-line',         label: 'FastAPI + React' },
        { icon: 'ri-file-chart-line',   label: 'Research & IEEE' },
        { icon: 'ri-team-line',         label: 'Team Lead' },
        { icon: 'ri-cpu-line',          label: 'MLOps' },
        { icon: 'ri-bar-chart-box-line',label: 'Data Pipelines' },
    ];

    return (
        <>
            <section className="home section" id="home">
                <div className="home__container container grid">
                    <h1 className={`home__name hero-fade hero-fade--name${heroVisible ? ' hero-fade--visible' : ''}`}>
                        Haider Masood
                    </h1>

                    <div className={`home__perfil hero-fade hero-fade--image${heroVisible ? ' hero-fade--visible' : ''}`}>
                        <div className="home__image">
                            <img src={`${process.env.PUBLIC_URL}/assets/img/home-perfil.jpg`} alt="Haider Masood Profile" className="home__img" loading="lazy" />
                            <div className="home__shadow"></div>
                            <img src={`${process.env.PUBLIC_URL}/assets/img/curved-arrow.svg`} alt="Home arrow" className="home__arrow" loading="lazy" />
                            <img src={`${process.env.PUBLIC_URL}/assets/img/random-lines.svg`} alt="Home random lines" className="home__line" loading="lazy" />
                            <div className="geometric-box"></div>
                        </div>
                    </div>

                    <div className={`home__info hero-fade hero-fade--desc${heroVisible ? ' hero-fade--visible' : ''}`}>
                        <p className="home__description">
                            <b className="home__typewriter">
                                {typewriterText}
                                <span className="home__cursor">|</span>
                            </b>
                            {' '}— I sculpt intelligent realities through data, models, and my expertise.
                        </p>
                        <div className="home__actions">
                            <a href="#about" className="button button__secondary" onClick={(e) => {
                                e.preventDefault();
                                document.getElementById('about').scrollIntoView({ behavior: 'smooth' });
                            }}>
                                Read About Me <i className="ri-arrow-down-line button-icon"></i>
                            </a>
                            <button onClick={handleResumeRequest} className="button button-flex">
                                Download Resume <i className="ri-download-line button-icon"></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── What I Build marquee ── */}
                <div className="marquee__wrapper">
                    <p className="marquee__label">What I Build</p>
                    <div className="marquee__track-outer">
                        <div className="marquee__track">
                            {/* Two copies for seamless loop */}
                            {[...marqueeItems, ...marqueeItems].map((item, idx) => (
                                <span className="marquee__item" key={idx}>
                                    <i className={`${item.icon} marquee__icon`}></i>
                                    <span className="marquee__text">{item.label}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Resume Request Modal */}
            {showResumeModal && (
                <div className="resume-modal-overlay" onClick={() => setShowResumeModal(false)}>
                    <div className="resume-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="resume-modal-header">
                            <h3>Download Resume</h3>
                            <button className="resume-modal-close" onClick={() => setShowResumeModal(false)}>
                                <i className="ri-close-line"></i>
                            </button>
                        </div>
                        <div className="resume-modal-content">
                            <p>Enter your email to download my resume:</p>
                            <form onSubmit={handleResumeDownload}>
                                <input
                                    type="email"
                                    placeholder="your.email@example.com"
                                    value={resumeEmail}
                                    onChange={(e) => setResumeEmail(e.target.value)}
                                    className="resume-email-input"
                                    required
                                />
                                {resumeMessage && (
                                    <p className={`resume-message ${resumeMessage.includes('✅') ? 'success' : 'error'}`}>
                                        {resumeMessage}
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    className="button resume-download-btn"
                                    disabled={isSubmittingResume}
                                >
                                    {isSubmittingResume ? 'Processing...' : 'Download Resume'}
                                    <i className="ri-download-line"></i>
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Home;
